import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  useAddCollaborateur,
  useDeleteCollaborateur,
  useUpdateCollaborateur,
} from "@/query/collaborateurQuery"
import { useShopDataQuery } from "@/query/shopDataQuery"
import {
  CollaborateurWithType,
  setOpenModalCollaborateurCreate,
  setOpenModalCollaborateurDetails,
} from "@/store/modalsReducers/modalSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import {
  CollaborateurCard,
  CollaborateurCreateModal,
  CollaborateurDetailsModal,
  Select,
} from "@okeys/shared-react-components"
import { AdminShop, Manager, Prestatere } from "@okeys/shared-utils"
import { ExclamationTriangleIcon, PlusIcon } from "@radix-ui/react-icons"
import React, { useState } from "react"

export const Collaborateurs: React.FC = () => {
  const { shopData, pendingShopData, errorShopData } = useShopDataQuery()
  const dispatch = useAppDispatch()
  const modalDetailsState = useAppSelector(
    (state) => state.modal.openModalCollaborateurDetails
  )
  const modalCreateState = useAppSelector(
    (state) => state.modal.openModalCollaborateurCreate
  )
  const [activeFilter, setActiveFilter] = useState<
    "tous" | "prestataires" | "managers" | "admins"
  >("tous")

  // Mutations
  const addMutation = useAddCollaborateur()
  const updateMutation = useUpdateCollaborateur()
  const deleteMutation = useDeleteCollaborateur()

  const managers: Manager[] = shopData?.managers || []
  const prestataires: Prestatere[] = shopData?.prestateres || []
  const admins: AdminShop[] = shopData?.admins || []

  const getCollaborateurName = (
    collaborateur: CollaborateurWithType
  ): string => {
    switch (collaborateur.type) {
      case "manager":
        return `${collaborateur.data.givenName} ${collaborateur.data.familyName}`
      case "prestatere":
        return `${collaborateur.data.givenName} ${collaborateur.data.familyName}`
      case "admin":
        return `${collaborateur.data.givenName} ${collaborateur.data.familyName}`
    }
  }

  const getCollaborateurEmail = (
    collaborateur: CollaborateurWithType
  ): string => {
    return collaborateur.data.email || "Non renseigné"
  }

  const getCollaborateurRole = (
    collaborateur: CollaborateurWithType
  ): string => {
    switch (collaborateur.type) {
      case "manager":
        return collaborateur.data.isPrestatere
          ? "Manager & Prestataire"
          : "Manager"
      case "prestatere":
        return "Prestataire"
      case "admin":
        return collaborateur.data.isPrestatere
          ? "Admin & Prestataire"
          : "Administrateur"
    }
  }

  // Fonction pour formater les horaires
  const formatSchedule = (schedule: Record<string, unknown>) => {
    const dayNames = {
      monday: "Lundi",
      tuesday: "Mardi",
      wednesday: "Mercredi",
      thursday: "Jeudi",
      friday: "Vendredi",
      saturday: "Samedi",
      sunday: "Dimanche",
    }

    return Object.entries(schedule)
      .map(([day, dailySchedule]) => {
        const dayName = dayNames[day as keyof typeof dayNames]
        if (
          !dailySchedule ||
          (Array.isArray(dailySchedule) && dailySchedule.length === 0)
        ) {
          return `${dayName}: Fermé`
        }

        if (Array.isArray(dailySchedule)) {
          const formattedHours = dailySchedule
            .map(
              (slot: { start: string; end: string }) =>
                `${slot.start} - ${slot.end}`
            )
            .join(", ")
          return `${dayName}: ${formattedHours}`
        }

        return `${dayName}: ${dailySchedule}`
      })
      .join("\n")
  }

  const allCollaborateurs: CollaborateurWithType[] = [
    ...managers.map((m) => ({ type: "manager" as const, data: m })),
    ...prestataires.map((p) => ({ type: "prestatere" as const, data: p })),
    ...admins.map((a) => ({ type: "admin" as const, data: a })),
  ]

  // Composant de skeleton pour le chargement
  const CollaborateurSkeleton = () => (
    <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-center space-x-4">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-8" />
      </div>
    </div>
  )

  // Gestion de l'état d'erreur
  if (errorShopData) {
    return (
      <div className="space-y-6 w-full">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Collaborateurs
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Erreur lors du chargement des données
            </p>
          </div>
        </div>

        <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <ExclamationTriangleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
          <AlertDescription className="text-red-800 dark:text-red-200">
            Impossible de charger les informations des collaborateurs. Veuillez
            vérifier votre connexion et réessayer.
          </AlertDescription>
        </Alert>

        <div className="flex justify-center">
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
          >
            Réessayer
          </Button>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="h-full flex flex-col gap-4">
        {/* Liste des collaborateurs avec onglets */}
        {pendingShopData ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <CollaborateurSkeleton key={index} />
            ))}
          </div>
        ) : (
          <div className="h-full flex flex-col gap-4">
            <div className="flex flex-row gap-2 justify-between items-center w-full">
              <div className="flex items-center space-x-3 ">
                <Button
                  size="sm"
                  disabled={pendingShopData}
                  onClick={() => {
                    dispatch(setOpenModalCollaborateurCreate({ isOpen: true }))
                  }}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Ajouter un collaborateur
                </Button>
              </div>
              <div className="flex-shrink-0">
                <Select
                  value={activeFilter}
                  onChange={(value) => {
                    setActiveFilter(
                      value as "tous" | "prestataires" | "managers" | "admins"
                    )
                  }}
                  options={[
                    {
                      value: "tous",
                      label: `Tous (${allCollaborateurs.length})`,
                    },
                    {
                      value: "prestataires",
                      label: `Prestataires (${prestataires.length})`,
                    },
                    {
                      value: "managers",
                      label: `Managers (${managers.length})`,
                    },
                    { value: "admins", label: `Admins (${admins.length})` },
                  ]}
                  placeholder="Filtrer les collaborateurs"
                  className="w-60"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
              {activeFilter === "tous" &&
                (allCollaborateurs.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {allCollaborateurs.map((collab) => {
                      const collaborateurData = {
                        id: collab.data.id,
                        name: getCollaborateurName(collab),
                        email: getCollaborateurEmail(collab),
                        role: getCollaborateurRole(collab),
                        type: collab.type as "admin" | "manager" | "prestatere",
                        isPrestatere:
                          collab.type === "prestatere" ||
                          (collab.type === "manager" &&
                            collab.data.isPrestatere) ||
                          (collab.type === "admin" && collab.data.isPrestatere),
                        schedule: collab.data.schedule,
                      }
                      return (
                        <CollaborateurCard
                          key={collab.data.id}
                          collaborateur={collaborateurData}
                          onEdit={() => {
                            dispatch(
                              setOpenModalCollaborateurDetails({
                                isOpen: true,
                                collaborateur: collab,
                                variant: "edit",
                              })
                            )
                          }}
                          formatSchedule={formatSchedule}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun collaborateur trouvé
                  </div>
                ))}

              {activeFilter === "prestataires" &&
                (prestataires.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {prestataires.map((p) => {
                      const collaborateurData = {
                        id: p.id,
                        name: `${p.givenName} ${p.familyName}`,
                        email: p.email,
                        role: "Prestataire",
                        type: "prestatere" as const,
                        isPrestatere: true,
                        schedule: p.schedule,
                      }
                      return (
                        <CollaborateurCard
                          key={p.id}
                          collaborateur={collaborateurData}
                          onEdit={() => {
                            dispatch(
                              setOpenModalCollaborateurDetails({
                                isOpen: true,
                                collaborateur: {
                                  type: "prestatere",
                                  data: p,
                                },
                                variant: "edit",
                              })
                            )
                          }}
                          formatSchedule={formatSchedule}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun prestataire trouvé
                  </div>
                ))}

              {activeFilter === "managers" &&
                (managers.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {managers.map((m) => {
                      const collaborateurData = {
                        id: m.id,
                        name: `${m.givenName} ${m.familyName}`,
                        email: m.email,
                        role: m.isPrestatere
                          ? "Manager & Prestataire"
                          : "Manager",
                        type: "manager" as const,
                        isPrestatere: m.isPrestatere,
                        schedule: m.schedule,
                      }
                      return (
                        <CollaborateurCard
                          key={m.id}
                          collaborateur={collaborateurData}
                          onEdit={() => {
                            dispatch(
                              setOpenModalCollaborateurDetails({
                                isOpen: true,
                                collaborateur: {
                                  type: "manager",
                                  data: m,
                                },
                                variant: "edit",
                              })
                            )
                          }}
                          formatSchedule={formatSchedule}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun manager trouvé
                  </div>
                ))}

              {activeFilter === "admins" &&
                (admins.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {admins.map((a) => {
                      const collaborateurData = {
                        id: a.id,
                        name: `${a.givenName} ${a.familyName}`,
                        email: a.email,
                        role: a.isPrestatere ? "Admin & Prestataire" : "Admin",
                        type: "admin" as const,
                        isPrestatere: a.isPrestatere,
                        schedule: a.schedule,
                      }
                      return (
                        <CollaborateurCard
                          key={a.id}
                          collaborateur={collaborateurData}
                          onEdit={() => {
                            dispatch(
                              setOpenModalCollaborateurDetails({
                                isOpen: true,
                                collaborateur: { type: "admin", data: a },
                                variant: "edit",
                              })
                            )
                          }}
                          formatSchedule={formatSchedule}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun administrateur trouvé
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Modal de détails/édition collaborateur */}
        <CollaborateurDetailsModal
          open={modalDetailsState.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              dispatch(
                setOpenModalCollaborateurDetails({
                  isOpen: false,
                  collaborateur: null,
                })
              )
            }
          }}
          collaborateur={
            modalDetailsState.collaborateur
              ? {
                  id: modalDetailsState.collaborateur.data.id,
                  givenName: modalDetailsState.collaborateur.data.givenName,
                  familyName: modalDetailsState.collaborateur.data.familyName,
                  email: modalDetailsState.collaborateur.data.email || "",
                  role: modalDetailsState.collaborateur.type as
                    | "admin"
                    | "manager"
                    | "prestatere",
                  isPrestatere:
                    modalDetailsState.collaborateur.type === "prestatere" ||
                    (modalDetailsState.collaborateur.type === "manager" &&
                      modalDetailsState.collaborateur.data.isPrestatere) ||
                    (modalDetailsState.collaborateur.type === "admin" &&
                      modalDetailsState.collaborateur.data.isPrestatere),
                  schedule: modalDetailsState.collaborateur.data.schedule,
                }
              : null
          }
          shopSchedule={shopData?.shopSchedule}
          onSave={async (updatedCollaborateur) => {
            try {
              await updateMutation.mutateAsync({
                collaborateurId: updatedCollaborateur.id,
                collaborateur: {
                  givenName: updatedCollaborateur.givenName,
                  familyName: updatedCollaborateur.familyName,
                  email: updatedCollaborateur.email,
                  role:
                    updatedCollaborateur.role === "prestatere"
                      ? "prestatere"
                      : (updatedCollaborateur.role as
                          | "manager"
                          | "admin"
                          | "prestatere"),
                  isPrestatere: updatedCollaborateur.isPrestatere,
                  schedule: updatedCollaborateur.schedule,
                },
              })
              dispatch(
                setOpenModalCollaborateurDetails({
                  isOpen: false,
                  collaborateur: null,
                })
              )
            } catch (error) {
              console.error("Erreur lors de la mise à jour:", error)
            }
          }}
          onDelete={async (collaborateurToDelete) => {
            try {
              await deleteMutation.mutateAsync({
                collaborateurId: collaborateurToDelete.id,
              })
              dispatch(
                setOpenModalCollaborateurDetails({
                  isOpen: false,
                  collaborateur: null,
                })
              )
            } catch (error) {
              console.error("Erreur lors de la suppression:", error)
            }
          }}
        />

        {/* Modal de création collaborateur */}
        <CollaborateurCreateModal
          open={modalCreateState.isOpen}
          onOpenChange={(open) => {
            dispatch(setOpenModalCollaborateurCreate({ isOpen: open }))
          }}
          shopSchedule={shopData?.shopSchedule}
          onSave={async (newCollaborateur) => {
            try {
              await addMutation.mutateAsync({
                collaborateur: {
                  givenName: newCollaborateur.givenName,
                  familyName: newCollaborateur.familyName,
                  email: newCollaborateur.email,
                  role:
                    newCollaborateur.role === "prestatere"
                      ? "prestatere"
                      : (newCollaborateur.role as
                          | "manager"
                          | "admin"
                          | "prestatere"),
                  isPrestatere: newCollaborateur.isPrestatere,
                  schedule: newCollaborateur.schedule,
                },
              })
              dispatch(setOpenModalCollaborateurCreate({ isOpen: false }))
            } catch (error) {
              console.error("Erreur lors de la création:", error)
            }
          }}
          onCancel={() => {
            dispatch(setOpenModalCollaborateurCreate({ isOpen: false }))
          }}
        />
      </div>
    </TooltipProvider>
  )
}
